import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import {
  getWorkspaceGetAgentQueryKey,
  getWorkspaceListAgentsQueryKey,
  useWorkspaceCreateAgent,
  useWorkspaceDeleteAgent,
  useWorkspaceDuplicateAgent,
  useWorkspaceUpdateAgent,
} from '@/generated/hooks/rain/v1/workspace_scoped/agent_service';
import {
  useWorkspaceCreateThread,
  workspaceListThreads,
} from '@/generated/hooks/rain/v1/workspace_scoped/thread_service';
import {
  workspaceCreateArtifactStore,
  workspaceListArtifactStoreKinds,
  workspaceListArtifactStores,
} from '@/generated/hooks/rain/v1/workspace_scoped/artifact_catalog_service';
import {
  getWorkspaceListMemoryBlocksQueryKey,
  useWorkspaceCreateMemoryBlock,
  useWorkspaceUpdateMemoryBlock,
  workspaceListMemoryBlocks,
} from '@/generated/hooks/rain/v1/workspace_scoped/memory_service';
import {
  useWorkspaceSetGenerator,
  workspaceGetGenerator,
  workspaceGetStaticGeneratorConfigSchema,
  workspaceListGeneratorKinds,
} from '@/generated/hooks/rain/v1/workspace_scoped/generator_service';
import {
  getWorkspaceListToolSourcesQueryKey,
  getWorkspaceListToolsQueryKey,
  useWorkspaceCreateToolSource,
  useWorkspaceDeleteToolSource,
  workspaceCreateToolSource,
  workspaceEnableTools,
  workspaceListTools,
  workspaceListToolSources,
} from '@/generated/hooks/rain/v1/workspace_scoped/tool_catalog_service';
import type {
  V1ConfigField,
  V1ScopeLevel,
} from '@/generated/hooks/rain/models';
import { useCreateAsset } from '@/generated/hooks/bishop/collections/v1/api';
import type { V1Asset } from '@/generated/hooks/bishop/models';
import { uploadFileToBishop } from '@/shared/utils/file-upload';
import {
  AUTOMATIONS_TOOL_SOURCE_URL,
  BDK_TOOL_SOURCE_URL,
  type IAgentTemplate,
} from '@/modules/agent-builder/constants';
import {
  KNOWLEDGE_FILES_BLOCK_NAME,
  KNOWLEDGE_FILES_BLOCK_TYPE,
  mergeKnowledgeFiles,
  parseKnowledgeFiles,
  removeKnowledgeFile,
  serializeKnowledgeFiles,
  type IKnowledgeFile,
} from '@/modules/agent-builder/knowledge-files';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { checkIsNotFoundError } from '@/shared/utils/error-handler';

import { BUILDER_CONFIG_THREAD_MARKER } from '../constants';
import { plannedAutomationsForTemplate } from '../template-automations';
import {
  PLANNED_AUTOMATIONS_BLOCK_NAME,
  serializePlannedAutomations,
} from '../planned-automations';

/**
 * Memory-block kinds the wizard writes. Agent config lives in typed blocks on
 * the config thread (verified against the backend), not a generic list:
 *   - text (name "description") → "Goal" (Describe step, config.content)
 *   - system_instructions       → "Instructions"
 *   - text (name "Memory")      → "Memory" (free-text notes)
 *   - persona                   → "Personality" (markdown behavior, config.content)
 *   - tone_style                → tone/style pair (dynamic enum; kept for hydration)
 *
 * NOTE: personality writes the `persona` block via `config.content` (preset
 * markdown per personality — see PERSONA_CONTENT), so it must not share a type
 * with the free-text Memory block, which uses `text`.
 *
 * The backend only knows a small set of block *types* — the Describe-step goal
 * therefore also uses `text` (there is no `description` type), and is told apart
 * from the free-text Memory block by its `config.name` ("description"). Because
 * a block's `block_id` is a deterministic hash of its name, same-type blocks
 * with different names are distinct resources; `upsertMemoryBlock` matches on
 * (type, name) so they never clobber each other.
 */
export const MEMORY_BLOCK_TYPE = {
  description: 'text',
  instructions: 'system_instructions',
  memory: 'text',
  persona: 'persona',
  tone: 'tone_style',
} as const;

/**
 * `config.name` for blocks whose `type` alone isn't unique. Two `text` blocks
 * (Goal vs Memory) coexist on a thread, distinguished by this name.
 */
export const MEMORY_BLOCK_NAME = {
  description: 'description',
  memory: 'Memory',
} as const;

/**
 * Tool-source kind that binds a book (bdk) connection to the agent. A picked
 * connection is attached by creating a tool source of this type whose config
 * carries the chosen `connection_id`.
 */
const BOOK_TOOL_SOURCE_TYPE = 'connection';

/**
 * Tool-source kind that lets the agent INVOKE the workspace's published
 * automations (Grimoire + Jeeves). Attaching it offers every published + active
 * automation in the workspace as a tool the user can enable; nothing is enabled
 * by attaching, and detaching removes the capability entirely. Its config
 * carries the org/workspace scope (endpoints default in-cluster server-side),
 * mirroring the book/bdk source. This kind ships in the cloudburst Rain build.
 */
export const AUTOMATIONS_TOOL_SOURCE_TYPE = 'automations';

/**
 * Kind of the artifact store that backs Knowledge Files. Bishop is cloudburst's
 * artifact store; a `bishop://{id}--{filename}` uri is read verbatim by the
 * `artifacts` tool source's `artifact_read`.
 */
const BISHOP_ARTIFACT_STORE_TYPE = 'bishop';

/**
 * Kind of the tool source that exposes artifact read/list tools (so the agent
 * can `artifact_read` a registered Knowledge File's `bishop://` uri).
 */
const ARTIFACTS_TOOL_SOURCE_TYPE = 'artifacts';

/**
 * Substrings that identify the artifact READ / LIST tools on the `artifacts`
 * tool source. We enable exactly these (leaving write/edit/delete off) so a
 * Knowledge File is strictly read-only context. Matched case-insensitively
 * against the tool's original/display name.
 */
const ARTIFACT_READ_TOOL_HINTS = ['artifact_read', 'artifact_list'];

/**
 * Build a config object from a schema's fields, seeding each field from its
 * declared `default` (or the first static-enum option for required enums).
 * Fields without a usable default are omitted; the backend applies its own
 * defaults and validates required ones. Enough to satisfy a generator's
 * baseline config so Deploy can proceed.
 */
function buildConfigFromDefaults(
  fields: V1ConfigField[],
): Record<string, unknown> {
  const config: Record<string, unknown> = {};
  for (const field of fields) {
    if (!field.name) continue;
    if (field.default !== undefined && field.default !== null) {
      config[field.name] = field.default;
    } else if (field.required && field.static_enum?.[0]?.id) {
      config[field.name] = field.static_enum[0].id;
    }
  }
  return config;
}

/**
 * Human-readable `name` seeded onto a memory block at create time, keyed by
 * block type. The backend's config schema requires a `name` for these kinds,
 * but the wizard doesn't collect one, so we supply a sensible fixed label.
 *
 * Only covers types that are unique on a thread. The `text` type is shared by
 * two blocks (Goal vs Memory), so those callers pass an explicit `name` to
 * `upsertMemoryBlock` instead of relying on this map.
 *
 * NOTE: the schema-driven route (`memory-blocks:static-config`) isn't deployed
 * yet, so we can't fetch required fields at runtime — this hardcodes the one
 * required field we know about (`name`). Fold it into a schema lookup once that
 * endpoint ships.
 */
const MEMORY_BLOCK_DEFAULT_NAME: Record<string, string> = {
  [MEMORY_BLOCK_TYPE.instructions]: 'Instructions',
  [MEMORY_BLOCK_TYPE.persona]: 'Personality',
  [MEMORY_BLOCK_TYPE.tone]: 'Tone & Style',
};

/**
 * Identify the "Knowledge Files" registry block. It's a `text` block matched by
 * its `config.name` (the `text` type is shared with the free-text Memory block,
 * so type alone isn't enough).
 */
const isKnowledgeFilesBlock = (block: {
  type?: string;
  config?: unknown;
}): boolean =>
  block.type === KNOWLEDGE_FILES_BLOCK_TYPE &&
  (block.config as { name?: string } | undefined)?.name ===
    KNOWLEDGE_FILES_BLOCK_NAME;

/** The last memory-block write queued on each thread, so the next waits on it. */
const memoryBlockWrites = new Map<string, Promise<unknown>>();

/**
 * Runs a thread's memory-block writes one at a time, across every component.
 * Each write lists the blocks and then creates or updates, so two at once (the
 * builder seeding on entry, the user's first save) can both miss a block and
 * create it twice.
 *
 * @param threadId - The thread whose blocks the write changes.
 * @param write - The list-then-write to run once the earlier ones settle.
 * @returns The write's result.
 */
function queueMemoryBlockWrite<T>(
  threadId: string,
  write: () => Promise<T>,
): Promise<T> {
  const previous = memoryBlockWrites.get(threadId) ?? Promise.resolve();
  const next = previous.catch(() => undefined).then(write);
  memoryBlockWrites.set(threadId, next);
  void next
    .catch(() => undefined)
    .finally(() => {
      if (memoryBlockWrites.get(threadId) === next) {
        memoryBlockWrites.delete(threadId);
      }
    });
  return next;
}

/**
 * Thin workspace-scoped wrapper over the Rain agent/thread hooks used by the
 * creation wizard. Keeps the id-threading (organization/workspace) in one
 * place so the wizard and its steps stay declarative.
 *
 * The lifecycle it supports mirrors the backend model: create an empty agent,
 * open a config thread on it, apply config to the thread (rename + memory),
 * then Deploy to bake a runnable version.
 */
export function useAgentBuilderApi() {
  const organization = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspace = useUserPreferencesStore(selectCurrentWorkspaceId);
  const queryClient = useQueryClient();

  const { mutateAsync: createAgentAsync } = useWorkspaceCreateAgent();
  const { mutateAsync: updateAgentAsync } = useWorkspaceUpdateAgent();
  const { mutateAsync: deleteAgentAsync } = useWorkspaceDeleteAgent();
  const { mutateAsync: duplicateAgentAsync } = useWorkspaceDuplicateAgent();
  const { mutateAsync: createThreadAsync } = useWorkspaceCreateThread();
  const { mutateAsync: createMemoryBlockAsync } =
    useWorkspaceCreateMemoryBlock();
  const { mutateAsync: updateMemoryBlockAsync } =
    useWorkspaceUpdateMemoryBlock();
  const { mutateAsync: setGeneratorAsync } = useWorkspaceSetGenerator();
  const { mutateAsync: createToolSourceAsync } = useWorkspaceCreateToolSource();
  const { mutateAsync: deleteToolSourceAsync } = useWorkspaceDeleteToolSource();
  const { mutateAsync: createAssetAsync } = useCreateAsset();

  /** Create an empty agent and return its id. */
  const createAgent = useCallback(
    async (name: string): Promise<string> => {
      const res = await createAgentAsync({
        organization,
        workspace,
        data: { name },
      });
      const id = res.data.id;
      if (!id) throw new Error('CreateAgent returned no id');
      return id;
    },
    [createAgentAsync, organization, workspace],
  );

  /** Drop the cached agent list so a create/rename/duplicate/delete shows up. */
  const invalidateAgentList = useCallback(
    () =>
      queryClient.invalidateQueries({
        // Partial key (no params) so every page-size/filter variant of the
        // list is refetched, not just the dashboard's current one.
        queryKey: getWorkspaceListAgentsQueryKey(organization, workspace),
      }),
    [organization, queryClient, workspace],
  );

  /**
   * Rename an agent. Renaming is the only direct edit to a base agent — every
   * other field is deployable config edited on a thread — so this is a plain
   * PATCH of the display name.
   *
   * NOTE: the agent's *id* is derived from its name at create time and does NOT
   * change here, so a renamed agent keeps its original id (and its URL).
   */
  const renameAgent = useCallback(
    async (agentId: string, name: string): Promise<void> => {
      await updateAgentAsync({
        organization,
        workspace,
        agentId,
        data: { name },
      });
      await Promise.all([
        invalidateAgentList(),
        // The builder header reads the name off the single-agent GET, so that
        // query has to refetch too — not just the dashboard list.
        queryClient.invalidateQueries({
          queryKey: getWorkspaceGetAgentQueryKey(
            organization,
            workspace,
            agentId,
          ),
        }),
      ]);
    },
    [
      invalidateAgentList,
      organization,
      queryClient,
      updateAgentAsync,
      workspace,
    ],
  );

  /** Duplicate an agent, returning the copy's id. Backend names it "Copy of …". */
  const duplicateAgent = useCallback(
    async (agentId: string, name?: string): Promise<string | undefined> => {
      const res = await duplicateAgentAsync({
        organization,
        workspace,
        agentId,
        data: name ? { name } : {},
      });
      await invalidateAgentList();
      return res.data.id;
    },
    [duplicateAgentAsync, invalidateAgentList, organization, workspace],
  );

  /** Permanently delete an agent and drop it from the cached list. */
  const deleteAgent = useCallback(
    async (agentId: string): Promise<void> => {
      await deleteAgentAsync({ organization, workspace, agentId });
      await invalidateAgentList();
    },
    [deleteAgentAsync, invalidateAgentList, organization, workspace],
  );

  /**
   * Refresh both the tool-sources list and the tools list for a config thread.
   * Any change to a tool source (attach/remove a connection, automations, or
   * artifacts) changes what tools the thread exposes, so the derived tools list
   * must refetch alongside the sources list.
   */
  const invalidateToolLists = useCallback(
    (agentId: string, threadId: string) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: getWorkspaceListToolSourcesQueryKey(
            organization,
            workspace,
            agentId,
            threadId,
          ),
        }),
        queryClient.invalidateQueries({
          queryKey: getWorkspaceListToolsQueryKey(
            organization,
            workspace,
            agentId,
            threadId,
          ),
        }),
      ]),
    [organization, queryClient, workspace],
  );

  /**
   * Make the workspace's published automations *available* to the agent by
   * creating an `automations` tool source on the config thread. Idempotent —
   * if one already exists, returns that `source_id` without stacking a
   * duplicate. The source auto-discovers every published + active automation in
   * the workspace and lists each as an offered tool; the org/workspace scope is
   * passed in config (endpoints default in-cluster).
   *
   * Attaching grants NO access on its own. Rain reads enablement from a list
   * keyed by source id and treats an absent entry as "expose no tools", so a
   * freshly attached source contributes zero callable tools. Every newly created
   * agent gets this attached in the background from `resolveConfigThread`, which
   * only means the automations show up in the Tools pane — the user must enable
   * each automation there before the agent can run it.
   */
  const attachAutomations = useCallback(
    async (agentId: string, threadId: string): Promise<string> => {
      const existing = await workspaceListToolSources(
        organization,
        workspace,
        agentId,
        threadId,
      );
      const already = (existing.data.tool_sources ?? []).find(
        s => s.type === AUTOMATIONS_TOOL_SOURCE_TYPE,
      );
      if (already?.source_id) return already.source_id;

      const res = await createToolSourceAsync({
        organization,
        workspace,
        agentId,
        threadId,
        data: {
          type: AUTOMATIONS_TOOL_SOURCE_TYPE,
          config: {
            organization_id: organization,
            workspace_id: workspace,
            url: AUTOMATIONS_TOOL_SOURCE_URL,
          },
        },
      });
      const sourceId = res.data.source_id;
      if (!sourceId) throw new Error('CreateToolSource returned no source_id');

      // Attaching adds the automation tools, so refresh both sources and tools.
      await invalidateToolLists(agentId, threadId);
      return sourceId;
    },
    [createToolSourceAsync, invalidateToolLists, organization, workspace],
  );

  /**
   * Resolve the config/test thread for an agent: reuse the one carrying the
   * builder's marker, or create a fresh one. Returns the thread id.
   *
   * The lookup filters on `BUILDER_CONFIG_THREAD_MARKER` instead of taking
   * whatever thread is newest, so a real conversation the user started on the
   * agent is never adopted as the builder's sandbox. ListThreads returns
   * threads sorted by `updated_at` descending (per the V1Thread proto), so if
   * a race ever left two marked threads the most recent one wins.
   *
   * Side effect: fires a best-effort, background `attachAutomations` so the
   * workspace's automations are listed in every new agent's Tools pane. This
   * grants no access — each automation stays disabled until the user enables it.
   * It's intentionally NOT awaited — creation/navigation must not wait on it — and
   * failures are swallowed (automations capability is otherwise handled
   * reactively; if the Rain build lacks the `automations` kind the attach just
   * no-ops). `attachAutomations` is idempotent, so re-resolving an existing
   * thread won't stack duplicate sources.
   */
  const resolveConfigThread = useCallback(
    async (agentId: string): Promise<string> => {
      const existing = await workspaceListThreads(
        organization,
        workspace,
        agentId,
        { filter: `description == "${BUILDER_CONFIG_THREAD_MARKER}"` },
      );
      const threads = existing.data.threads ?? [];
      // Marked threads only, most-recent-first (see the doc comment above);
      // take the first one that carries an id.
      let id = threads.find(thread => thread.thread_id)?.thread_id;
      if (!id) {
        const created = await createThreadAsync({
          organization,
          workspace,
          agentId,
          // Marks this as the builder's internal config/test thread, not a
          // real conversation, so the threads console can filter it out. See
          // `BUILDER_CONFIG_THREAD_MARKER`'s doc comment for why the marker
          // lives on `description` rather than `variables`.
          data: { description: BUILDER_CONFIG_THREAD_MARKER },
        });
        id = created.data.thread_id;
      }
      if (!id) throw new Error('CreateThread returned no thread_id');

      // Fire-and-forget: offer the workspace's automations in the background so
      // they're always listed for the user to enable. Not awaited (must not
      // block creation) and best-effort.
      void attachAutomations(agentId, id).catch(() => {});

      return id;
    },
    [attachAutomations, createThreadAsync, organization, workspace],
  );

  /** List the memory blocks currently on a thread (with their block_ids). */
  const listThreadMemoryBlocks = useCallback(
    async (agentId: string, threadId: string) => {
      const res = await workspaceListMemoryBlocks(
        organization,
        workspace,
        agentId,
        threadId,
      );
      return res.data.memory_blocks ?? [];
    },
    [organization, workspace],
  );

  /**
   * Upsert a typed, agent-scoped memory block, given its full config object (the
   * config field names differ per kind — e.g. text/persona/system_instructions
   * use `content`, tone_style uses `tone` + `style`).
   *
   * Matching differs by kind:
   *   - Singleton kinds (persona / system_instructions / tone_style) are unique
   *     per agent AND their config schema has NO `name` field — the backend
   *     silently drops any `name` we send (see the "ignoring unknown config
   *     field" warnings). So they MUST be matched by `type` alone; matching on
   *     `config.name` never finds them (it's always undefined server-side),
   *     which would wrongly fall through to a create and fail to update the
   *     seeded block. This is why the Builder's personality write appeared to
   *     no-op.
   *   - `text` is used by two distinct blocks on one thread (Goal vs Memory),
   *     so those ARE matched by (type, name) via the caller-supplied `name`.
   *
   * If a matching block exists it is updated, otherwise created.
   *
   * Pass `scope: 'SCOPE_LEVEL_THREAD'` for a block that belongs to one thread
   * only; it then matches thread-scoped blocks alone, never an agent-wide one.
   *
   * NOTE: agent-scoped blocks MUST go through MemoryService — the thread-memory
   * API (`updateThreadMemory`) rejects them with 412.
   */
  const writeMemoryBlock = useCallback(
    async (
      agentId: string,
      threadId: string,
      type: string,
      config: Record<string, unknown>,
      name?: string,
      scope: V1ScopeLevel = 'SCOPE_LEVEL_AGENT',
    ): Promise<void> => {
      const resolvedName = name ?? MEMORY_BLOCK_DEFAULT_NAME[type];
      const blocks = await listThreadMemoryBlocks(agentId, threadId);
      // Singleton identity kinds carry no server-side `name`, so match them by
      // `type` alone; only the `text` kind is shared by two blocks on a thread
      // (Goal vs Memory) and must be disambiguated by config.name.
      const isSingleton = type !== MEMORY_BLOCK_TYPE.memory;
      const existing = blocks.find(
        b =>
          b.type === type &&
          b.block_id &&
          (scope !== 'SCOPE_LEVEL_THREAD' || b.scope === scope) &&
          (isSingleton ||
            (b.config as { name?: string } | undefined)?.name === resolvedName),
      );
      if (existing?.block_id) {
        await updateMemoryBlockAsync({
          organization,
          workspace,
          agentId,
          threadId,
          blockId: existing.block_id,
          data: { config },
        });
      } else {
        // A fresh block must satisfy the kind's config schema, which requires
        // a `name` the wizard doesn't collect. Seed the resolved label so the
        // backend doesn't reject with "missing required config field: name".
        // A caller-provided `name` in `config` still wins.
        const seededConfig = {
          name: resolvedName,
          ...config,
        };
        await createMemoryBlockAsync({
          organization,
          workspace,
          agentId,
          threadId,
          data: { type, config: seededConfig, scope },
        });
      }
      // Refetch so a step revisited later re-hydrates from the written value.
      await queryClient.invalidateQueries({
        queryKey: getWorkspaceListMemoryBlocksQueryKey(
          organization,
          workspace,
          agentId,
          threadId,
        ),
      });
    },
    [
      createMemoryBlockAsync,
      listThreadMemoryBlocks,
      organization,
      queryClient,
      updateMemoryBlockAsync,
      workspace,
    ],
  );

  /** {@link writeMemoryBlock}, queued behind the thread's other block writes. */
  const upsertMemoryBlock = useCallback(
    (
      agentId: string,
      threadId: string,
      type: string,
      config: Record<string, unknown>,
      name?: string,
      scope: V1ScopeLevel = 'SCOPE_LEVEL_AGENT',
    ): Promise<void> =>
      queueMemoryBlockWrite(threadId, () =>
        writeMemoryBlock(agentId, threadId, type, config, name, scope),
      ),
    [writeMemoryBlock],
  );

  /**
   * Guarantee the thread has at least one per-thread config customization so
   * Deploy doesn't 412 with "thread has no per-thread config customizations".
   *
   * Reads the thread's current generator; if it's not already a per-thread
   * customization, re-applies it at thread scope via SetGenerator (copying the
   * inherited type+config), which registers a deployable customization. If the
   * agent has no generator at all, sets one from the first available kind using
   * its live static-schema defaults. Returns true if it wrote a customization.
   *
   * Config is schema-driven (`{[k]: unknown}`); we read live values/defaults
   * rather than hard-coding keys.
   */
  const ensureDeployableCustomization = useCallback(
    async (agentId: string, threadId: string): Promise<boolean> => {
      let currentType: string | undefined;
      let currentConfig: Record<string, unknown> | undefined;
      let alreadyCustomized = false;
      try {
        const current = await workspaceGetGenerator(
          organization,
          workspace,
          agentId,
          threadId,
        );
        currentType = current.data.type;
        currentConfig = current.data.config as Record<string, unknown>;
        alreadyCustomized = Boolean(current.data.is_customized);
      } catch (error) {
        // Only a 404 means "no generator yet" (fall through to seed one).
        // Network/auth/5xx errors must NOT be swallowed — treating a transient
        // failure as "no generator" would overwrite a valid generator config.
        if (!checkIsNotFoundError(error)) throw error;
      }

      if (alreadyCustomized) return false;

      let type = currentType;
      let config = currentConfig;
      if (!type) {
        const kindsRes = await workspaceListGeneratorKinds(
          organization,
          workspace,
        );
        type = kindsRes.data.kinds?.[0]?.type;
        if (!type) throw new Error('No generator kinds available');
        const schemaRes = await workspaceGetStaticGeneratorConfigSchema(
          organization,
          workspace,
          agentId,
          { type, thread_id: threadId },
        );
        config = buildConfigFromDefaults(schemaRes.data.config_fields ?? []);
      }

      await setGeneratorAsync({
        organization,
        workspace,
        agentId,
        threadId,
        data: { type, config: config ?? {} },
      });
      return true;
    },
    [organization, workspace, setGeneratorAsync],
  );

  /**
   * Attach a book connection to the agent by creating a `bdk` tool source on
   * the config thread whose config carries the chosen `connection_id`. This is
   * how a picked connection is "established" for the agent. Reuses an existing
   * tool source for the same `connection_id` instead of attaching it twice.
   * Returns the tool source's `source_id` so it can later be removed.
   */
  const attachBookConnection = useCallback(
    async (
      agentId: string,
      threadId: string,
      connectionId: string,
    ): Promise<string> => {
      const existing = await workspaceListToolSources(
        organization,
        workspace,
        agentId,
        threadId,
      );
      const already = (existing.data.tool_sources ?? []).find(
        s => s.config?.connection_id === connectionId,
      );
      if (already?.source_id) return already.source_id;

      const res = await createToolSourceAsync({
        organization,
        workspace,
        agentId,
        threadId,
        data: {
          type: BOOK_TOOL_SOURCE_TYPE,
          config: {
            connection_id: connectionId,
            url: BDK_TOOL_SOURCE_URL,
            organization_id: organization,
            workspace_id: workspace,
          },
        },
      });
      const sourceId = res.data.source_id;
      if (!sourceId) throw new Error('CreateToolSource returned no source_id');
      // A new connection adds tools, so refresh both the sources and the
      // tools list (the Connect step's toggle list).
      await invalidateToolLists(agentId, threadId);
      return sourceId;
    },
    [createToolSourceAsync, invalidateToolLists, organization, workspace],
  );

  /**
   * Remove a previously-attached tool source (book connection) from the agent
   * by deleting it from the config thread (DELETE /tool-sources/{sourceId}).
   */
  const removeToolSource = useCallback(
    async (
      agentId: string,
      threadId: string,
      sourceId: string,
    ): Promise<void> => {
      await deleteToolSourceAsync({
        organization,
        workspace,
        agentId,
        threadId,
        sourceId,
      });
      // Removing a connection drops its tools, so refresh both the sources and
      // the tools list (the Connect step's toggle list).
      await invalidateToolLists(agentId, threadId);
    },
    [deleteToolSourceAsync, invalidateToolLists, organization, workspace],
  );

  /**
   * Remove the agent's ability to invoke automations by deleting the
   * `automations` tool source(s) from the config thread. No-op if none present.
   */
  const detachAutomations = useCallback(
    async (agentId: string, threadId: string): Promise<void> => {
      const list = await workspaceListToolSources(
        organization,
        workspace,
        agentId,
        threadId,
      );
      const sources = (list.data.tool_sources ?? []).filter(
        (s): s is typeof s & { source_id: string } =>
          s.type === AUTOMATIONS_TOOL_SOURCE_TYPE && Boolean(s.source_id),
      );
      for (const source of sources) {
        await deleteToolSourceAsync({
          organization,
          workspace,
          agentId,
          threadId,
          sourceId: source.source_id,
        });
      }
      await invalidateToolLists(agentId, threadId);
    },
    [deleteToolSourceAsync, invalidateToolLists, organization, workspace],
  );

  /**
   * Pre-seed the target agent's identity memory blocks (Personality,
   * Instructions, Tone & Style) so a downstream writer only ever *overwrites*
   * existing blocks. Used by the conversational Agent Builder: the Builder's
   * `set_memory_config` tool upserts these blocks, and seeding them up front
   * keeps that a pure update against a known block rather than a create.
   *
   * Idempotent — `upsertMemoryBlock` matches on (type, name), so a block that
   * already carries content is refreshed with empty content ONLY when missing.
   * We check first and seed only the absent ones, so re-opening the room never
   * clobbers content the Builder already wrote.
   *
   * NOTE: the user's opening prompt is deliberately NOT seeded into
   * Instructions. That text is a request addressed to the Builder ("build me an
   * AP clerk"), not instructions addressed to the agent being built — writing it
   * verbatim leaves the agent configured to obey the request instead of to do
   * the job. It goes to the Builder chat only; the Builder's `set_*` tools
   * author the real instructions from it.
   *
   * NOTE: tone/style are deliberately NOT seeded. They're enums the user is
   * never asked about at this point, and pre-filling them (previously
   * "empathetic"/"verbose") presented arbitrary values as decisions the user had
   * made. The block is created only once something actually sets it.
   */
  const ensureIdentityBlocks = useCallback(
    (agentId: string, threadId: string): Promise<void> =>
      queueMemoryBlockWrite(threadId, async () => {
        const blocks = await listThreadMemoryBlocks(agentId, threadId);
        // Identity blocks are singletons whose configs carry no server-side
        // `name`, so presence is by `type` alone (matching upsertMemoryBlock).
        const hasBlock = (type: string): boolean =>
          blocks.some(b => b.type === type);
        // `name` is omitted — upsertMemoryBlock falls back to
        // MEMORY_BLOCK_DEFAULT_NAME[type] for these singleton identity kinds.
        const seeds: {
          type: string;
          config: Record<string, unknown>;
        }[] = [
          { type: MEMORY_BLOCK_TYPE.persona, config: { content: '' } },
          { type: MEMORY_BLOCK_TYPE.instructions, config: { content: '' } },
        ];
        // Seed only the absent blocks. Sequential (not parallel) so two seeds that
        // both list-then-create can't race on the same thread.
        for (const seed of seeds) {
          if (hasBlock(seed.type)) continue;
          await writeMemoryBlock(agentId, threadId, seed.type, seed.config);
        }
      }),
    [listThreadMemoryBlocks, writeMemoryBlock],
  );

  /**
   * Apply a prebuilt template's full configuration to a freshly created agent.
   *
   * Unlike `ensureIdentityBlocks` (which seeds empty placeholders and skips any
   * block that already exists), this WRITES the template's authored content —
   * instructions, persona, tone/style, the goal, and the role's planned
   * automations — so the agent arrives at the Builder already configured
   * rather than as an empty shell holding only a first chat message.
   *
   * Sequential, not parallel: `upsertMemoryBlock` does a list-then-create/update
   * against the same thread, so concurrent writes can both miss an existing
   * block and race to create it. Ordered most-important-first so a mid-way
   * failure still leaves the agent with its instructions.
   *
   * Throws on the first failed write. Callers that must not block navigation
   * should catch — the Builder can still fill in whatever didn't land, since
   * `upsertMemoryBlock` creates on miss.
   */
  const seedTemplateConfig = useCallback(
    async (
      agentId: string,
      threadId: string,
      template: IAgentTemplate,
    ): Promise<void> => {
      await upsertMemoryBlock(
        agentId,
        threadId,
        MEMORY_BLOCK_TYPE.instructions,
        { content: template.instructions.trim() },
      );
      await upsertMemoryBlock(agentId, threadId, MEMORY_BLOCK_TYPE.persona, {
        content: template.persona.trim(),
      });
      await upsertMemoryBlock(
        agentId,
        threadId,
        MEMORY_BLOCK_TYPE.description,
        { content: template.description.trim() },
        MEMORY_BLOCK_NAME.description,
      );
      const planned = plannedAutomationsForTemplate(template);
      if (planned.length) {
        await upsertMemoryBlock(
          agentId,
          threadId,
          'text',
          {
            name: PLANNED_AUTOMATIONS_BLOCK_NAME,
            content: serializePlannedAutomations(planned),
          },
          PLANNED_AUTOMATIONS_BLOCK_NAME,
        );
      }
      // Tone/style last: it's the only block a user is never blocked by, and
      // unlike the others it carries no `content` field (tone + style in config).
      await upsertMemoryBlock(agentId, threadId, MEMORY_BLOCK_TYPE.tone, {
        tone: template.tone,
        style: template.style,
      });
    },
    [upsertMemoryBlock],
  );

  /**
   * Write the goal (Describe step) to the agent's `description` memory block via
   * MemoryService (upsert). The goal lives in a typed, agent-scoped block like
   * the other identity fields — not the thread's agent card — so it hydrates the
   * same way and is promoted by Deploy along with the rest of the config.
   */
  const setThreadDescription = useCallback(
    (agentId: string, threadId: string, description: string): Promise<void> =>
      upsertMemoryBlock(
        agentId,
        threadId,
        MEMORY_BLOCK_TYPE.description,
        { content: description },
        MEMORY_BLOCK_NAME.description,
      ),
    [upsertMemoryBlock],
  );

  // --- Knowledge Files (agent-level documents the agent can read) ----------

  /** Write a Knowledge Files list back to the target agent's registry block. */
  const writeKnowledgeFiles = useCallback(
    (
      agentId: string,
      threadId: string,
      files: IKnowledgeFile[],
    ): Promise<void> =>
      upsertMemoryBlock(
        agentId,
        threadId,
        KNOWLEDGE_FILES_BLOCK_TYPE,
        { content: serializeKnowledgeFiles(files) },
        KNOWLEDGE_FILES_BLOCK_NAME,
      ),
    [upsertMemoryBlock],
  );

  /**
   * Idempotently wire the plumbing a Knowledge File needs on the target agent:
   *   1. a Bishop **artifact store** (so a `bishop://` uri resolves), and
   *   2. an **`artifacts` tool source** with its read/list tools enabled (so
   *      the agent can `artifact_read` a registered file), and
   *   3. the **"Knowledge Files" text memory block** (the registry that lists
   *      attached files in every thread's context).
   *
   * Safe to call before every attach. Each step checks for the resource first
   * and creates only what's missing.
   *
   * Fallback: on an essential/local build Bishop may not be an available store
   * kind. We don't hard-fail — the store step is skipped, the registry block +
   * `artifacts` source are still set up, and the target (cloudburst) is where
   * Bishop exists. The registry block is the source of truth either way.
   */
  const ensureKnowledgeSetup = useCallback(
    async (agentId: string, threadId: string): Promise<void> => {
      // 1) Bishop artifact store (best-effort; skip if Bishop isn't available).
      try {
        const stores = await workspaceListArtifactStores(
          organization,
          workspace,
          agentId,
          threadId,
        );
        const hasBishop = (stores.data.stores ?? []).some(
          s => s.type === BISHOP_ARTIFACT_STORE_TYPE,
        );
        if (!hasBishop) {
          const kindsRes = await workspaceListArtifactStoreKinds(
            organization,
            workspace,
          );
          const bishopAvailable = (kindsRes.data.kinds ?? []).some(
            k => k.type === BISHOP_ARTIFACT_STORE_TYPE,
          );
          if (bishopAvailable) {
            await workspaceCreateArtifactStore(
              organization,
              workspace,
              agentId,
              threadId,
              {
                store_id: BISHOP_ARTIFACT_STORE_TYPE,
                type: BISHOP_ARTIFACT_STORE_TYPE,
                config: {},
              },
            );
          }
        }
      } catch {
        // A store failure must not block registering the file — the registry
        // block below is what lists it in-context; artifact_read resolution is
        // a runtime concern on the (cloudburst) deploy target.
      }

      // 2) `artifacts` tool source + enable its read/list tools.
      try {
        const sources = await workspaceListToolSources(
          organization,
          workspace,
          agentId,
          threadId,
        );
        let artifactsSource = (sources.data.tool_sources ?? []).find(
          s => s.type === ARTIFACTS_TOOL_SOURCE_TYPE,
        );
        if (!artifactsSource?.source_id) {
          const created = await workspaceCreateToolSource(
            organization,
            workspace,
            agentId,
            threadId,
            { type: ARTIFACTS_TOOL_SOURCE_TYPE, config: {} },
          );
          artifactsSource = created.data;
        }
        const artifactsSourceId = artifactsSource?.source_id;
        if (artifactsSourceId) {
          const toolsRes = await workspaceListTools(
            organization,
            workspace,
            agentId,
            threadId,
            { source_id: artifactsSourceId },
          );
          const readListIds = (toolsRes.data.tools ?? [])
            .filter(tool => {
              const name = (
                tool.original_name ??
                tool.name ??
                ''
              ).toLowerCase();
              return ARTIFACT_READ_TOOL_HINTS.some(hint => name.includes(hint));
            })
            .map(tool => tool.id)
            .filter((id): id is string => Boolean(id));
          // EnableTools is additive, so a re-run never disables tools a user
          // turned on in the Tools pane.
          if (readListIds.length > 0) {
            await workspaceEnableTools(
              organization,
              workspace,
              agentId,
              threadId,
              artifactsSourceId,
              { tool_ids: readListIds },
            );
          }
        }
        await invalidateToolLists(agentId, threadId);
      } catch {
        // As above — a source/enablement hiccup shouldn't block registration.
      }

      // 3) Ensure the registry block exists (empty header when first created).
      const blocks = await listThreadMemoryBlocks(agentId, threadId);
      if (!blocks.some(isKnowledgeFilesBlock)) {
        await writeKnowledgeFiles(agentId, threadId, []);
      }
    },
    [
      invalidateToolLists,
      listThreadMemoryBlocks,
      organization,
      workspace,
      writeKnowledgeFiles,
    ],
  );

  /**
   * Read the current Knowledge Files list off the target agent's registry
   * block (parsing `- <name> (<uri>)` lines). Returns [] when the block is
   * absent or empty.
   */
  const listKnowledgeFiles = useCallback(
    async (agentId: string, threadId: string): Promise<IKnowledgeFile[]> => {
      const blocks = await listThreadMemoryBlocks(agentId, threadId);
      const block = blocks.find(isKnowledgeFilesBlock);
      return parseKnowledgeFiles(block?.content);
    },
    [listThreadMemoryBlocks],
  );

  /**
   * Register one or more assets as Knowledge Files: ensure setup, then merge
   * the incoming {name, uri} into the registry block (de-duped by uri) and
   * write it back. Returns the full, post-merge file list.
   */
  const addKnowledgeFiles = useCallback(
    async (
      agentId: string,
      threadId: string,
      incoming: IKnowledgeFile[],
    ): Promise<IKnowledgeFile[]> => {
      await ensureKnowledgeSetup(agentId, threadId);
      const existing = await listKnowledgeFiles(agentId, threadId);
      const merged = mergeKnowledgeFiles(existing, incoming);
      await writeKnowledgeFiles(agentId, threadId, merged);
      return merged;
    },
    [ensureKnowledgeSetup, listKnowledgeFiles, writeKnowledgeFiles],
  );

  /**
   * Upload local files to Bishop (2-step: upload bytes → create asset), then
   * register the resulting assets as Knowledge Files. Used by the "Upload" tab
   * of the file picker, whose `onUploadFiles` hands raw `File`s to the consumer.
   * Returns the created assets so the caller can reflect them immediately.
   */
  const uploadAndRegisterFiles = useCallback(
    async (
      agentId: string,
      threadId: string,
      files: File[],
    ): Promise<V1Asset[]> => {
      const assets: V1Asset[] = [];
      for (const file of files) {
        const uploaded = await uploadFileToBishop(
          file,
          organization,
          workspace,
        );
        const res = await createAssetAsync({
          organization,
          workspace,
          data: {
            asset: { display_name: file.name },
            file_name: uploaded.resourceName,
          },
        });
        assets.push(res.data);
      }
      const incoming = assets
        .filter(a => a.reference_uri)
        .map(a => ({ name: a.display_name, uri: a.reference_uri ?? '' }));
      if (incoming.length > 0) {
        await addKnowledgeFiles(agentId, threadId, incoming);
      }
      return assets;
    },
    [addKnowledgeFiles, createAssetAsync, organization, workspace],
  );

  /**
   * Remove the Knowledge File whose name (or uri) matches `name` from the
   * registry block. Returns the remaining file list.
   */
  const removeKnowledgeFileByName = useCallback(
    async (
      agentId: string,
      threadId: string,
      name: string,
    ): Promise<IKnowledgeFile[]> => {
      const existing = await listKnowledgeFiles(agentId, threadId);
      const next = removeKnowledgeFile(existing, name);
      await writeKnowledgeFiles(agentId, threadId, next);
      return next;
    },
    [listKnowledgeFiles, writeKnowledgeFiles],
  );

  return {
    organization,
    workspace,
    createAgent,
    renameAgent,
    duplicateAgent,
    deleteAgent,
    resolveConfigThread,
    listThreadMemoryBlocks,
    upsertMemoryBlock,
    ensureIdentityBlocks,
    seedTemplateConfig,
    ensureDeployableCustomization,
    attachBookConnection,
    removeToolSource,
    attachAutomations,
    detachAutomations,
    setThreadDescription,
    ensureKnowledgeSetup,
    listKnowledgeFiles,
    addKnowledgeFiles,
    uploadAndRegisterFiles,
    removeKnowledgeFileByName,
  };
}
